import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Breadcrumb, Button, Drawer, Dropdown, Layout, Menu, Typography } from "@arco-design/web-react";
import { useQueryClient } from "@tanstack/react-query";
import { Menu as MenuIcon, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { clearHttpAccessToken } from "../../lib/http/accessToken";
import { HTTP_AUTH_EXPIRED_EVENT } from "../../lib/http/axios";
import { BusinessPageTitleContext, type BusinessPageTitle } from "../../lib/navigation/useBusinessPageTitle";
import { isHttpClientError } from "../../lib/http/errors";
import {
  buildAuthReturnPath,
  resetAuthSessionQueries,
  resolveAuthUserAvatarText,
  resolveAuthUserDisplayName,
  resolveAuthUserRoleLabel,
  useCurrentAuthUser,
} from "../../features/auth";
import {
  adminNavigationConfig,
  filterAdminNavigationConfig,
  getAdminNavigationGroups,
  type AdminNavigationItem,
} from "./admin.config";
import { useAdminNavigation } from "./useAdminNavigation";
import { AdminTagsView } from "./AdminTagsView.tsx";
import {
  normalizeAdminTagPathname,
  resolveAdminMenuHref,
  writeAdminTagsToStorage,
} from "./adminTagsViewState";
import { NotificationCenterTrigger } from "../../features/notification-center";
import "./AdminLayout.css";

const { Sider, Header, Content } = Layout;
const { Title, Text } = Typography;

const sidebarWidth = 260;
const collapsedSidebarWidth = 64;
const compactBreakpoint = 1280;
const quoteBudgetStackedMaxWidth = 860;
/** 与商机/联系人移动卡片断点一致：该宽度及以下隐藏后台顶栏与多标签。 */
const listWorkbenchMobileChromeMaxWidth = 760;
const ADMIN_CHROME_IMMERSIVE_STORAGE_KEY = "flowermaster.admin.chrome-immersive";

/** 后台内容页可经由 Outlet 调用的壳层能力。 */
export type AdminOutletContext = {
  setHeaderActions: (actions: ReactNode) => void;
  openAdminNavigation?: () => void;
};

/** 读取本会话是否处于整站沉浸。 */
function readChromeImmersiveFromStorage(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.sessionStorage.getItem(ADMIN_CHROME_IMMERSIVE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/** 写入整站沉浸偏好。 */
function writeChromeImmersiveToStorage(immersive: boolean) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (immersive) {
      window.sessionStorage.setItem(ADMIN_CHROME_IMMERSIVE_STORAGE_KEY, "1");
    } else {
      window.sessionStorage.removeItem(ADMIN_CHROME_IMMERSIVE_STORAGE_KEY);
    }
  } catch {
    // 忽略配额/隐私模式失败。
  }
}

/**
 * 统一后台布局壳层。
 */
export function AdminLayout() {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const siderCollapsedBeforeAutoCollapseRef = useRef<boolean | null>(null);
  const [headerActions, setHeaderActions] = useState<ReactNode>(null);
  const [businessPageTitle, setBusinessPageTitle] = useState<BusinessPageTitle | null>(null);
  const [contentRefreshKey, setContentRefreshKey] = useState(0);
  const [chromeImmersive, setChromeImmersive] = useState(() => readChromeImmersiveFromStorage());
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < compactBreakpoint : false,
  );
  const [isListWorkbenchMobileViewport, setIsListWorkbenchMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= listWorkbenchMobileChromeMaxWidth : false,
  );
  const [isStackedBudgetViewport, setIsStackedBudgetViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= quoteBudgetStackedMaxWidth : false,
  );
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigation = useAdminNavigation();
  const pageTitle = businessPageTitle?.pathname === location.pathname
    ? businessPageTitle.title
    : navigation.pageTitle;
  const currentUserQuery = useCurrentAuthUser();
  const currentUser = currentUserQuery.data;
  const currentUserDisplayName = resolveAuthUserDisplayName(currentUser);
  const currentUserAvatarText = resolveAuthUserAvatarText(currentUser);
  const currentUserRoleLabel = resolveAuthUserRoleLabel(currentUser);
  const isWorkbenchRoute = isWideWorkbenchPath();
  const isMobileWorkbenchRoute = isCustomizationMobileWorkbenchPath(location.pathname);
  const isListWorkbenchMobileChrome =
    isListWorkbenchMobileChromePath(location.pathname) && isListWorkbenchMobileViewport;
  const isCustomizationPcWorkbenchRoute = isCustomizationPcWorkbenchPath(location.pathname);
  const shouldHideAdminChrome = isMobileWorkbenchRoute || isListWorkbenchMobileChrome;
  const useCompactShell = isMobile || isWorkbenchRoute || shouldHideAdminChrome;
  const shouldShowCompactWorkbenchHeader = isWorkbenchRoute && !isMobile;
  const hideDesktopSider = useCompactShell || chromeImmersive;
  const hideDesktopHeader = shouldHideAdminChrome || chromeImmersive;
  const visibleNavigationItems = useMemo(
    () => filterAdminNavigationConfig(adminNavigationConfig, currentUser),
    [currentUser],
  );
  const menuGroups = useMemo(() => getAdminNavigationGroups(visibleNavigationItems), [visibleNavigationItems]);
  const selectedItem = useMemo(
    () => adminNavigationConfig.find((item) => item.key === navigation.selectedKey),
    [navigation.selectedKey],
  );
  const selectedParentKeys = useMemo(() => selectedItem?.parentKeys ?? [], [selectedItem]);
  useEffect(() => {
    const previousTitle = document.title;
    const routeTitle = `${pageTitle} | 花儿研学`;
    document.title = routeTitle;
    return () => {
      if (document.title === routeTitle) document.title = previousTitle;
    };
  }, [pageTitle]);
  // 路由变化时自动展开祖先；用户折叠时以手动 openKeys 为准，不再强制合并回去。
  useEffect(() => {
    setOpenKeys((previous) => Array.from(new Set([...previous, ...selectedParentKeys])));
  }, [navigation.selectedKey, selectedParentKeys]);
  // 定制工作台与行程核价编辑页默认收成图标侧栏以腾出内容宽度；离开后恢复进入前偏好。不走隐藏侧栏的 compact 壳层。
  const shouldAutoCollapseSider =
    isCustomizationPcWorkbenchRoute || isQuoteBudgetEditorPath(location.pathname) || isCustomerQuoteEditorPath(location.pathname);
  useEffect(() => {
    if (shouldAutoCollapseSider) {
      setSiderCollapsed((current) => {
        if (siderCollapsedBeforeAutoCollapseRef.current === null) {
          siderCollapsedBeforeAutoCollapseRef.current = current;
        }
        return true;
      });
      return;
    }
    if (siderCollapsedBeforeAutoCollapseRef.current === null) {
      return;
    }
    const restore = siderCollapsedBeforeAutoCollapseRef.current;
    siderCollapsedBeforeAutoCollapseRef.current = null;
    setSiderCollapsed(restore);
  }, [shouldAutoCollapseSider]);
  // 分组节点本身不进 selectedKeys，避免与祖先分组同时呈现“选中”。
  const menuSelectedKeys = useMemo(() => {
    const isFolderKey = adminNavigationConfig.some((item) =>
      item.parentKeys?.includes(navigation.selectedKey),
    );
    return isFolderKey ? [] : [navigation.selectedKey];
  }, [navigation.selectedKey]);
  const useLockedFullWidthContent =
    (isQuoteBudgetEditorPath(location.pathname) && !isStackedBudgetViewport) || isHotelMapPath(location.pathname);
  const useFullWidthContent =
    useLockedFullWidthContent ||
    isQuoteBudgetEditorPath(location.pathname) ||
    isCustomerQuoteEditorPath(location.pathname) ||
    isFulfillmentOrderEditorPath(location.pathname);
  // PC 定制工作台保留侧栏；内容区内边距与页高偏移需扣掉顶栏+标签，避免确认区被整页滚动顶出视口。
  const lockCustomizationWorkbenchShell = isCustomizationPcWorkbenchPath(location.pathname);
  const outletContext = useMemo<AdminOutletContext>(
    () => ({
      setHeaderActions,
      openAdminNavigation: () => setDrawerVisible(true),
    }),
    [],
  );
  const contentShellStyle = {
    padding:
      isListWorkbenchMobileChrome
        ? 0
        : shouldHideAdminChrome || useFullWidthContent
          ? 0
          : chromeImmersive
            ? "8px 10px"
            : isWorkbenchRoute || lockCustomizationWorkbenchShell
              ? "6px 8px 8px"
              : "12px 10px",
    flex: "1 1 auto",
    minHeight: 0,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    // 列表工作台移动端只让内容壳滚动；子级勿再设 overflow-x:hidden，否则会叠出第二条滚动条。
    overflowX: isListWorkbenchMobileChrome || useLockedFullWidthContent ? "hidden" : undefined,
    overflowY: useLockedFullWidthContent ? "hidden" : "auto",
    boxSizing: "border-box",
    "--customization-workbench-shell-offset": shouldHideAdminChrome
      ? "0px"
      : lockCustomizationWorkbenchShell
        ? "104px"
        : isWorkbenchRoute
          ? "52px"
          : "50px",
  } as CSSProperties;

  const redirectToExpiredLogin = useCallback(() => {
    clearHttpAccessToken();
    resetAuthSessionQueries(queryClient);
    navigate("/auth/login?reason=expired", {
      replace: true,
      state: { from: buildAuthReturnPath(location) },
    });
  }, [location, navigate, queryClient]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < compactBreakpoint);
      setIsListWorkbenchMobileViewport(window.innerWidth <= listWorkbenchMobileChromeMaxWidth);
      setIsStackedBudgetViewport(window.innerWidth <= quoteBudgetStackedMaxWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    writeChromeImmersiveToStorage(chromeImmersive);
  }, [chromeImmersive]);

  useEffect(() => {
    if (!chromeImmersive) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setChromeImmersive(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [chromeImmersive]);

  const handleToggleChromeImmersive = useCallback(() => {
    setChromeImmersive((current) => !current);
  }, []);

  useEffect(() => {
    const error = currentUserQuery.error;
    if (!currentUserQuery.isError || !isHttpClientError(error) || error.statusCode !== 401) {
      return;
    }
    redirectToExpiredLogin();
  }, [currentUserQuery.error, currentUserQuery.isError, redirectToExpiredLogin]);

  useEffect(() => {
    window.addEventListener(HTTP_AUTH_EXPIRED_EVENT, redirectToExpiredLogin);
    return () => {
      window.removeEventListener(HTTP_AUTH_EXPIRED_EVENT, redirectToExpiredLogin);
    };
  }, [redirectToExpiredLogin]);

  const handleNavigate = (path: string) => {
    const currentPathname =
      typeof window === "undefined"
        ? location.pathname
        : window.location.pathname;
    const currentSearch =
      typeof window === "undefined" ? location.search : window.location.search;
    const { href, tags } = resolveAdminMenuHref(path, {
      currentPathname: normalizeAdminTagPathname(currentPathname),
      currentSearch: currentSearch || undefined,
    });
    writeAdminTagsToStorage(tags);
    navigate(href);
    setDrawerVisible(false);
  };

  const handleChromeToggle = () => {
    if (useCompactShell) {
      setDrawerVisible(true);
      return;
    }
    setSiderCollapsed((current) => !current);
  };

  const chromeToggleLabel = useCompactShell
    ? "打开导航菜单"
    : siderCollapsed
      ? "展开侧栏"
      : "收起侧栏";
  const chromeToggleIcon = useCompactShell ? (
    <MenuIcon size={18} />
  ) : siderCollapsed ? (
    <PanelLeftOpen size={18} />
  ) : (
    <PanelLeftClose size={18} />
  );

  const menuContent = (
    <div
      className="admin-shell__menu"
      style={{
        display: "flex",
        height: "100%",
        minHeight: 0,
        flexDirection: "column",
        background: "#ffffff",
      }}
    >
      <div
        className={`admin-shell__brand${siderCollapsed && !useCompactShell ? " is-collapsed" : ""}`}
        style={{
          borderBottom: "1px solid #e5e6eb",
          padding: siderCollapsed && !useCompactShell ? "16px 12px" : "24px 20px 18px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: siderCollapsed && !useCompactShell ? 0 : 14, justifyContent: "center" }}>
          <div
            style={{
              display: "flex",
              height: siderCollapsed && !useCompactShell ? 36 : 44,
              width: siderCollapsed && !useCompactShell ? 36 : 44,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 14,
              background: "#1d2129",
              color: "#ffffff",
              fontWeight: 700,
              flex: "none",
            }}
          >
            花
          </div>
          {siderCollapsed && !useCompactShell ? null : (
            <div>
              <Title heading={5} style={{ margin: 0, color: "#1d2129" }}>
                花儿研学
              </Title>
              <Text style={{ color: "#86909c", fontSize: 13 }}>管理后台</Text>
            </div>
          )}
        </div>
      </div>

      <div
        className="admin-shell__scroll"
        data-testid="admin-sidebar-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: siderCollapsed && !useCompactShell ? "hidden" : undefined,
          padding: siderCollapsed && !useCompactShell ? "12px 0 20px" : "16px 12px 20px",
        }}
      >
        {menuGroups.map((group) => (
          <div key={group.group} style={{ marginBottom: 18 }}>
            {siderCollapsed && !useCompactShell ? null : (
              <div
                style={{
                  padding: "0 12px 8px",
                  color: "#86909c",
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                }}
              >
                {group.group}
              </div>
            )}
            <Menu
              collapse={siderCollapsed && !useCompactShell}
              openKeys={siderCollapsed && !useCompactShell ? [] : openKeys}
              selectedKeys={menuSelectedKeys}
              style={{ border: "none", background: "transparent", width: "100%" }}
              onClickSubMenu={(_, nextOpenKeys) => setOpenKeys(nextOpenKeys)}
              triggerProps={
                siderCollapsed && !useCompactShell
                  ? {
                      className: "admin-shell-menu-pop",
                      position: "rt",
                      showArrow: false,
                      autoAlignPopupMinWidth: false,
                      mouseEnterDelay: 50,
                      mouseLeaveDelay: 80,
                      popupAlign: { right: [4, 0] },
                    }
                  : undefined
              }
            >
              {buildMenuTree(group.items).map((node) =>
                renderMenuNode(node, menuSelectedKeys[0] ?? "", handleNavigate, siderCollapsed && !useCompactShell),
              )}
            </Menu>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Layout
      className={`admin-shell${chromeImmersive ? " is-immersive" : ""}${
        isListWorkbenchMobileChromePath(location.pathname) ? " is-list-workbench-mobile" : ""
      }`}
      data-testid="admin-shell"
      data-immersive={chromeImmersive ? "true" : "false"}
      data-list-workbench-mobile={
        isListWorkbenchMobileChromePath(location.pathname) ? "true" : "false"
      }
      style={{
        height: "100vh",
        background: "#f7f8fa",
        overflow: "hidden",
      }}
    >
      {hideDesktopSider ? null : (
        <Sider
          className="admin-shell__sider"
          width={sidebarWidth}
          collapsed={siderCollapsed}
          collapsedWidth={collapsedSidebarWidth}
          collapsible={false}
          style={{
            height: "100vh",
            overflow: "hidden",
            background: "#ffffff",
            borderRight: "1px solid #e5e6eb",
          }}
        >
          {menuContent}
        </Sider>
      )}

      <Layout className="admin-shell__main" style={{ height: "100vh", minWidth: 0, overflow: "hidden" }}>
        {hideDesktopHeader ? null : (
        <Header
          data-testid="admin-header"
          className="admin-shell__header"
          style={{
            height: 50,
            borderBottom: "1px solid #e5e6eb",
            background: isWorkbenchRoute || useFullWidthContent ? "#f8fafc" : "#ffffff",
            padding: "0 12px",
            minHeight: 50,
            boxSizing: "border-box",
            boxShadow: isWorkbenchRoute || useFullWidthContent ? "0 1px 2px rgba(15, 23, 42, 0.04)" : undefined,
          }}
        >
          <div
            className="admin-shell__header-row"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              minWidth: 0,
              height: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth: 0,
                flex: 1,
              }}
            >
              <Button
                type="text"
                size="small"
                className="admin-shell__chrome-toggle"
                icon={chromeToggleIcon}
                aria-label={chromeToggleLabel}
                aria-pressed={useCompactShell ? undefined : siderCollapsed}
                onClick={handleChromeToggle}
              />
              <div style={{ minWidth: 0, overflow: "hidden" }}>
                <Breadcrumb>
                  {(shouldShowCompactWorkbenchHeader
                    ? navigation.breadcrumbs.slice(-2)
                    : navigation.breadcrumbs
                  ).map((item) => (
                    <Breadcrumb.Item key={item.label}>{item.label}</Breadcrumb.Item>
                  ))}
                </Breadcrumb>
              </div>
            </div>

            <div
              style={{
                display: shouldShowCompactWorkbenchHeader ? "none" : "flex",
                alignItems: "center",
                gap: 12,
                flexShrink: 0,
                flexWrap: "nowrap",
                justifyContent: "flex-end",
              }}
            >
              {headerActions ? (
                <div
                  data-testid="admin-header-actions"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  {headerActions}
                </div>
              ) : null}
              <NotificationCenterTrigger />
              <Dropdown
                trigger="click"
                position="br"
                droplist={(
                  <Menu
                    data-testid="admin-user-menu"
                    onClickMenuItem={(key) => {
                      if (key === "reset-password") {
                        handleNavigate("/auth/reset-password");
                        return;
                      }
                      if (key === "logout") {
                        handleNavigate("/auth/logout");
                      }
                    }}
                  >
                    <Menu.Item key="reset-password">重置密码</Menu.Item>
                    <Menu.Item key="logout">退出登录</Menu.Item>
                  </Menu>
                )}
              >
                <button
                  type="button"
                  data-testid="admin-user-menu-trigger"
                  aria-label="打开用户菜单"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    margin: 0,
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                  }}
                >
                  <Avatar size={32} style={{ backgroundColor: "#e8f3ff", color: "#165dff" }}>
                    {currentUserAvatarText}
                  </Avatar>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#1d2129", fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>
                      {currentUserDisplayName}
                    </div>
                    <Text style={{ color: "#86909c", fontSize: 12 }}>{currentUserRoleLabel}</Text>
                  </div>
                </button>
              </Dropdown>
            </div>
          </div>
        </Header>
        )}

        {shouldHideAdminChrome ? null : (
          <AdminTagsView
            pageTitle={pageTitle}
            onRefresh={() => setContentRefreshKey((current) => current + 1)}
            immersive={chromeImmersive}
            onToggleImmersive={handleToggleChromeImmersive}
          />
        )}

        <Content
          data-testid="admin-content-shell"
          style={contentShellStyle}
        >
          <div
            data-testid="admin-content-inner"
            style={{
              margin: 0,
              width: "100%",
              maxWidth: "none",
              height: useLockedFullWidthContent ? "100%" : undefined,
              minWidth: 0,
              boxSizing: "border-box",
            }}
          >
            <BusinessPageTitleContext.Provider value={setBusinessPageTitle}>
              <Outlet key={contentRefreshKey} context={outletContext} />
            </BusinessPageTitleContext.Provider>
          </div>
        </Content>
      </Layout>

      <Drawer
        width={sidebarWidth}
        title={null}
        footer={null}
        visible={drawerVisible}
        onCancel={() => setDrawerVisible(false)}
      >
        {menuContent}
      </Drawer>
    </Layout>
  );
}

/** PC CRM/定制工作台统一保留常驻侧栏；仅手机定制全屏路由走 compact 壳层。 */
function isWideWorkbenchPath(): boolean {
  return false;
}

/** 判断是否为 PC 定制工作台路径（含新建草稿、商机关联定制工作台）。 */
function isCustomizationPcWorkbenchPath(pathname: string): boolean {
  return (
    /^\/crm\/customization\/projects\/[^/]+\/workbench(?:\/.*)?$/.test(pathname) ||
    /^\/crm\/opportunities\/[^/]+\/customization-projects\/[^/]+\/workbench(?:\/.*)?$/.test(pathname)
  );
}

/** 核价预算编辑器铺满并锁定后台内容区，由编辑器内部处理滚动。 */
function isQuoteBudgetEditorPath(pathname: string): boolean {
  return /^\/quote\/budgets\/[^/]+$/.test(pathname);
}

/** 行程报价铺满后台内容区，由后台壳层承担长页面滚动。 */
function isCustomerQuoteEditorPath(pathname: string): boolean {
  return /^\/quote\/customer-quotes\/[^/]+$/.test(pathname);
}

/** 确认件编辑器铺满后台内容区，由编辑器内部处理响应式布局。 */
function isFulfillmentOrderEditorPath(pathname: string): boolean {
  return /^\/supply-chain\/fulfillment-orders\/workspaces\/[^/]+$/.test(pathname);
}

/** 酒店地图精确铺满并锁定后台内容区，由地图页面承载全部交互。 */
function isHotelMapPath(pathname: string): boolean {
  return pathname === "/resources/stay-map";
}

/** 判断是否为商机工作台列表路径（桌面表格与移动卡片共用同一路由）。 */
function isOpportunityWorkbenchPath(pathname: string): boolean {
  return pathname === "/crm/opportunities/workbench";
}

/** 判断是否为联系人工作台列表路径（桌面表格与移动卡片共用同一路由）。 */
function isContactWorkbenchPath(pathname: string): boolean {
  return pathname === "/crm/contacts/workbench";
}

/** 判断是否为课程线路列表路径（桌面表格与移动卡片共用同一路由）。 */
function isCourseRoutesPath(pathname: string): boolean {
  return pathname === "/product-lines/course-routes";
}

/** 窄屏下列表工作台隐藏后台 chrome 的路径集合。 */
function isListWorkbenchMobileChromePath(pathname: string): boolean {
  return isOpportunityWorkbenchPath(pathname)
    || isContactWorkbenchPath(pathname)
    || isCourseRoutesPath(pathname);
}

/** 判断是否为手机端临时需求定制全屏路由。 */
function isCustomizationMobileWorkbenchPath(pathname: string): boolean {
  return /^\/crm\/customization-mobile\/projects\/[^/]+\/(?:workbench|traces|itinerary)(?:\/.*)?$/.test(pathname);
}

type AdminMenuNode = AdminNavigationItem & {
  children: AdminMenuNode[];
};

/** 将平铺导航配置整理成当前分组内的菜单树。 */
function buildMenuTree(items: AdminNavigationItem[]): AdminMenuNode[] {
  const nodes = new Map<string, AdminMenuNode>();
  for (const item of items) {
    nodes.set(item.key, { ...item, children: [] });
  }

  const roots: AdminMenuNode[] = [];
  for (const item of items) {
    const node = nodes.get(item.key);
    if (!node) {
      continue;
    }
    const parentKey = item.parentKeys?.at(-1);
    const parent = parentKey ? nodes.get(parentKey) : undefined;
    if (parent) {
      parent.children.push(node);
      continue;
    }
    roots.push(node);
  }
  return roots;
}

/** 渲染后台导航菜单节点。 */
function renderMenuNode(
  item: AdminMenuNode,
  selectedKey: string,
  handleNavigate: (path: string) => void,
  collapsed = false,
) {
  if (item.children.length > 0) {
    return (
      <Menu.SubMenu
        key={item.key}
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            {item.icon}
            <span className="admin-shell__menu-label">{item.label}</span>
          </span>
        }
      >
        {item.children.map((child) => renderMenuNode(child, selectedKey, handleNavigate, collapsed))}
      </Menu.SubMenu>
    );
  }

  const isSelected = selectedKey === item.key;

  return (
    <Menu.Item
      key={item.key}
      style={{
        marginBottom: 6,
        borderRadius: collapsed ? 10 : 12,
        background: isSelected ? "#165dff" : "transparent",
        color: isSelected ? "#ffffff" : "#4e5969",
        fontWeight: isSelected ? 600 : 500,
      }}
      onClick={() => handleNavigate(item.path)}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
        }}
        title={collapsed ? item.label : undefined}
      >
        {item.icon}
        <span className="admin-shell__menu-label">{item.label}</span>
      </span>
    </Menu.Item>
  );
}
